import React, { useState } from 'react';
import { XCircle, MapPin } from 'lucide-react';

interface SurplusLandDisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
}

export default function SurplusLandDisclaimerModal({ isOpen, onClose, onProceed }: SurplusLandDisclaimerModalProps) {
  const [isChecked, setIsChecked] = useState(false);

  if (!isOpen) return null;

  const handleProceed = () => {
    if (isChecked) {
      onProceed();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <XCircle className="w-6 h-6" />
        </button>

        <div className="text-center mb-4">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-blue-100 rounded-full p-3">
              <MapPin className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-800">Surplus and/or Excess Land Notice</h2>
        </div>

        <div className="mb-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-blue-800 font-medium mb-3">Important Property Information</p>
            <p className="text-gray-700 mb-3">
              This property has been identified as having <strong>surplus and/or excess land</strong> beyond the typical operational
              requirements of a gas station and convenience store. This additional land may represent significant
              additional value or development potential.
            </p>
            <p className="text-gray-700 mb-3">
              <strong>Please note:</strong> Our valuation model focuses on the operational gas station business and
              may not fully capture the value of surplus and/or excess land. The actual property value could be substantially
              higher due to:
            </p>
            <ul className="text-gray-700 mb-3 ml-4 list-disc">
              <li>Development rights and zoning allowances</li>
              <li>Commercial real estate value of excess land</li>
              <li>Future expansion or subdivision potential</li>
              <li>Strategic location value</li>
            </ul>
            <p className="text-gray-700">
              We recommend consulting with a commercial real estate appraiser and local planning department
              to fully assess the value and development potential of the surplus and/or excess land.
            </p>
          </div>

          <div className="flex items-start mb-4">
            <div className="flex items-center h-5">
              <input
                id="surplus-land-checkbox"
                type="checkbox"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-blue-300"
                required
              />
            </div>
            <label htmlFor="surplus-land-checkbox" className="ml-2 text-sm text-gray-700">
              I understand that this property has surplus excess land and that the valuation may not fully reflect
              the total property value. I acknowledge the need for additional professional assessment of the surplus land value.
            </label>
          </div>
        </div>

        <div className="flex justify-center gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg bg-gray-300 text-gray-700 hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleProceed}
            disabled={!isChecked}
            className={`px-6 py-2 rounded-lg ${
              isChecked
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue with Valuation
          </button>
        </div>
      </div>
    </div>
  );
}